import React, { useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import type { LegalDocument } from '../data/legal';
type LegalModalProps = {
  document: LegalDocument | null;
  onClose: () => void;
};
export function LegalModal({
  document: doc,
  onClose
}: LegalModalProps) {
  const closeRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    if (!doc) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    const previousOverflow = window.document.body.style.overflow;
    window.document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.document.body.style.overflow = previousOverflow;
    };
  }, [doc, onClose]);
  return <AnimatePresence data-id="element-111" data-mp="1cspil9jd3mrk">
      {doc && <div className="fixed inset-0 z-50 flex items-end justify-center p-0 sm:items-center sm:p-6" data-id="element-112" data-mp="1cvv98t7n2wpe">
          <motion.div className="absolute inset-0 bg-ink/60" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} onClick={onClose} aria-hidden="true" data-id="element-113" data-mp="1asj5k5cshswm" />

          <motion.div role="dialog" aria-modal="true" aria-labelledby="legal-modal-title" className="relative flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-t-3xl bg-cream shadow-2xl sm:max-h-[85vh] sm:rounded-3xl" initial={{
        opacity: 0,
        y: 24,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 16,
        scale: 0.98
      }} transition={{
        duration: 0.25,
        ease: [0.23, 1, 0.32, 1]
      }} data-id="element-114" data-mp="s075xqdit5q6">
            <div className="flex items-start justify-between gap-6 border-b border-teal/15 bg-cream px-6 py-6 sm:px-10" data-id="element-115" data-mp="e7ilckdjxe7q">
              <h2 id="legal-modal-title" className="font-serif text-2xl font-semibold leading-tight text-ink sm:text-3xl" data-id="element-116" data-mp="669whvslfufr">
                {doc.title}
              </h2>
              <button ref={closeRef} type="button" onClick={onClose} aria-label="Cerrar" className="flex h-12 w-12 flex-none items-center justify-center rounded-full bg-paper text-inkSoft ring-1 ring-teal/15 transition-colors duration-150 ease-smooth hover:bg-teal-soft hover:text-teal-dark focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal" data-id="element-117" data-mp="fzqnyt59cxg9">
                <XIcon className="h-6 w-6" aria-hidden="true" data-id="element-118" data-mp="3oc660au1qjv5" />
              </button>
            </div>

            <div className="overflow-y-auto px-6 py-8 sm:px-10" data-id="element-119" data-mp="18ofuj7p31uze">
              <p className="font-sans text-lg leading-relaxed text-ink sm:text-xl" data-id="element-120" data-mp="13mgp7s9s8nzt">
                {doc.intro}
              </p>

              <div className="mt-8 space-y-8" data-id="element-121" data-mp="1b8ua73y32kpk">
                {doc.sections.map(section => <section key={section.heading} data-id="element-122" data-mp="1g3rgi33zafyr">
                    <h3 className="font-serif text-xl font-semibold text-ink sm:text-2xl" data-id="element-123" data-mp="126uuceyt1mo7">
                      {section.heading}
                    </h3>
                    <div className="mt-3 space-y-3" data-id="element-124" data-mp="169hpopuoppq5">
                      {section.paragraphs.map(paragraph => <p key={paragraph} className="font-sans text-lg leading-relaxed text-inkSoft" data-id="element-125" data-mp="1yw2g9o3c5xsn">
                          {paragraph}
                        </p>)}
                    </div>
                  </section>)}
              </div>
            </div>

            <div className="border-t border-teal/15 bg-cream px-6 py-5 sm:px-10" data-id="element-126" data-mp="2cu0jpyg4c9td">
              <button type="button" onClick={onClose} className="w-full rounded-xl bg-teal px-6 py-4 font-sans text-lg font-semibold text-white transition-colors duration-150 ease-smooth hover:bg-teal-dark focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-dark sm:w-auto" data-id="element-127" data-mp="2ho4ny9y023qr">
                Entendido, cerrar
              </button>
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}